import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, Sparkles, Check, Shield, Leaf, ChefHat, UserCog } from 'lucide-react';

const HERO_BG_STYLE = {
  backgroundColor: '#1f6d62',
  backgroundImage: `
    radial-gradient(circle at 20% 15%, rgba(160, 218, 57, 0.10) 0%, transparent 45%),
    radial-gradient(circle at 85% 80%, rgba(160, 218, 57, 0.10) 0%, transparent 50%),
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' seed='7'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.08 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")
  `,
};

const HeroBrushStrokes = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    <svg className="absolute -top-8 -left-12 w-64 h-24 opacity-20" viewBox="0 0 300 80" fill="none">
      <path d="M0 40 Q 75 10, 150 40 T 300 40" stroke="#a0da39" strokeWidth="3" strokeLinecap="round" />
    </svg>
    <svg className="absolute bottom-6 right-8 w-72 h-20 opacity-15" viewBox="0 0 300 80" fill="none">
      <path d="M0 40 Q 75 70, 150 40 T 300 40" stroke="#a0da39" strokeWidth="3" strokeLinecap="round" />
    </svg>
  </div>
);

export const SampleBoxHero = ({ boxSizes }) => {
  const navigate = useNavigate();

  // Calculate min and max samples from box sizes
  const minSamples = boxSizes.length > 0 ? Math.min(...boxSizes.map(b => b.size)) : 3;
  const maxSamples = boxSizes.length > 0 ? Math.max(...boxSizes.map(b => b.size)) : 5;
  const minPrice = boxSizes.length > 0 ? Math.min(...boxSizes.map(b => b.price)) : 2.99;

  const chips = [
    { icon: Leaf, label: 'Grain Free' },
    { icon: Shield, label: 'Vet Approved' },
    { icon: ChefHat, label: '11 Flavours' },
    { icon: Check, label: 'Made in UK' },
  ];

  return (
    <div className="relative overflow-hidden" style={HERO_BG_STYLE} data-testid="hero-section">
      {/* Admin Icon - Top Right */}
      <button
        onClick={() => navigate('/admin/login')}
        className="absolute top-4 right-4 w-10 h-10 bg-white/10 backdrop-blur-sm hover:bg-[#a0da39] hover:text-[#1f6d62] text-white rounded-full flex items-center justify-center transition-all duration-200 shadow-sm z-20 border border-white/20"
        title="Admin Login"
      >
        <UserCog className="w-5 h-5" />
      </button>
      <HeroBrushStrokes />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="flex flex-col md:flex-row items-center gap-10">
          {/* Text Content */}
          <div className="flex-1 text-center md:text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest text-[#1f6d62] bg-[#a0da39] mb-4">
                <Sparkles className="w-3 h-3" /> Sample Box Builder
              </span>
              <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.95]">
                Build Your Perfect
                <span className="text-[#a0da39] block">Sample Box</span>
              </h1>
              <p
                className="text-[#a0da39] mt-4 text-xl md:text-2xl italic"
                style={{ fontFamily: "'Caveat', 'Dancing Script', cursive" }}
              >
                Let your dog decide their favourite.
              </p>
              <p className="text-white/85 mt-4 text-sm md:text-base max-w-xl md:mx-0 mx-auto leading-relaxed">
                Pick your samples, fill your box, and try before you commit to a full bag.
              </p>

              {/* Benefit chips */}
              <div className="mt-6 flex flex-wrap justify-center md:justify-start gap-2">
                {chips.map((c) => (
                  <span
                    key={c.label}
                    className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 text-white text-xs font-semibold uppercase tracking-wide px-3 py-1.5 rounded-full backdrop-blur-sm"
                  >
                    <c.icon className="w-3.5 h-3.5 text-[#a0da39]" />
                    {c.label}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex-1 max-w-md w-full"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-[#a0da39]/25 blur-3xl rounded-full" />
              <img
                src="https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=600&q=80"
                alt="Happy dog"
                className="relative rounded-3xl shadow-2xl border-4 border-[#a0da39]"
              />
              <div className="absolute -bottom-4 -right-4 bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                <div className="flex items-center gap-2">
                  <Package className="w-6 h-6 text-[#0FA49C]" />
                  <div>
                    <p className="font-bold text-[#222] text-sm">From £{minPrice.toFixed(2)}</p>
                    <p className="text-[#666] text-xs">{minSamples === maxSamples ? `${minSamples} samples` : `${minSamples}-${maxSamples} samples`}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
